// @ts-nocheck - Temporary fix for authentication service types
interface TwoFactorSetup {
  userId: string;
  method: 'SMS' | 'EMAIL' | 'AUTHENTICATOR' | 'BACKUP_CODES';
  isEnabled: boolean;
  secret?: string; // For TOTP
  backupCodes?: string[];
  phoneNumber?: string;
  email?: string;
  createdAt: Date;
  lastUsed?: Date;
}

interface BiometricCredential {
  id: string;
  userId: string;
  type: 'FINGERPRINT' | 'FACE_ID' | 'VOICE' | 'IRIS';
  credentialId: string;
  publicKey: string;
  counter: number;
  deviceInfo: {
    userAgent: string;
    platform: string;
    deviceName?: string;
  };
  isActive: boolean;
  createdAt: Date;
  lastUsed?: Date;
}

interface AuthenticationAttempt {
  id: string;
  userId: string;
  method: 'PASSWORD' | 'TWO_FACTOR' | 'BIOMETRIC' | 'SOCIAL';
  success: boolean;
  ipAddress: string;
  userAgent: string;
  location?: {
    country: string;
    city: string;
    coordinates?: { lat: number; lng: number };
  };
  riskScore: number; // 0-100
  timestamp: Date;
  failureReason?: string;
}

interface SecuritySettings {
  userId: string;
  twoFactorRequired: boolean;
  biometricEnabled: boolean;
  sessionTimeout: number; // minutes
  allowedDevices: string[];
  trustedIPs: string[];
  securityNotifications: boolean;
  loginAlerts: boolean;
  suspiciousActivityAlerts: boolean;
  updatedAt: Date;
}

class AuthenticationService {
  private readonly STORAGE_KEY = 'authentication';

  // Two-Factor Authentication Setup
  async setupTwoFactor(userId: string, method: 'SMS' | 'EMAIL' | 'AUTHENTICATOR'): Promise<{
    secret?: string;
    qrCode?: string;
    backupCodes?: string[];
  }> {
    const existing = this.getTwoFactorSetup(userId);
    
    if (existing && existing.isEnabled) {
      throw new Error('Two-factor authentication is already enabled');
    }

    let setup: Partial<TwoFactorSetup> = {
      userId,
      method,
      isEnabled: false,
      createdAt: new Date()
    };

    switch (method) {
      case 'AUTHENTICATOR':
        const secret = this.generateTOTPSecret();
        const qrCode = this.generateQRCode(userId, secret);
        setup.secret = secret;
        
        return {
          secret,
          qrCode,
          backupCodes: this.generateBackupCodes()
        };

      case 'SMS':
        const phoneNumber = await this.getUserPhoneNumber(userId);
        if (!phoneNumber) {
          throw new Error('Phone number required for SMS 2FA');
        }
        setup.phoneNumber = phoneNumber;
        break;

      case 'EMAIL':
        const email = await this.getUserEmail(userId);
        setup.email = email;
        break;
    }

    this.saveTwoFactorSetup(userId, setup as TwoFactorSetup);
    return {};
  }

  async enableTwoFactor(userId: string, verificationCode: string): Promise<string[]> {
    const setup = this.getTwoFactorSetup(userId);
    if (!setup) {
      throw new Error('Two-factor authentication not set up');
    }

    const isValid = await this.verifyTwoFactorCode(userId, verificationCode);
    if (!isValid) {
      throw new Error('Invalid verification code');
    }

    setup.isEnabled = true;
    setup.backupCodes = this.generateBackupCodes();
    this.saveTwoFactorSetup(userId, setup);

    return setup.backupCodes;
  }

  async disableTwoFactor(userId: string, password: string): Promise<void> {
    // Verify password first
    const isValidPassword = await this.verifyPassword(userId, password);
    if (!isValidPassword) {
      throw new Error('Invalid password');
    }

    const setup = this.getTwoFactorSetup(userId);
    if (setup) {
      setup.isEnabled = false;
      this.saveTwoFactorSetup(userId, setup);
    }
  }

  async verifyTwoFactorCode(userId: string, code: string): Promise<boolean> {
    const setup = this.getTwoFactorSetup(userId);
    if (!setup || !setup.isEnabled) {
      return false;
    }

    switch (setup.method) {
      case 'AUTHENTICATOR':
        return this.verifyTOTP(setup.secret!, code);
      
      case 'SMS':
      case 'EMAIL':
        return this.verifyOTPCode(userId, code);
      
      case 'BACKUP_CODES':
        return this.verifyBackupCode(userId, code);
      
      default:
        return false;
    }
  }

  // Biometric Authentication
  async setupBiometric(userId: string, type: 'FINGERPRINT' | 'FACE_ID'): Promise<{
    challenge: string;
    options: any;
  }> {
    if (!this.isBiometricSupported(type)) {
      throw new Error(`${type} is not supported on this device`);
    }

    const challenge = this.generateChallenge();
    const options = this.createCredentialOptions(userId, challenge, type);

    return { challenge, options };
  }

  async registerBiometric(
    userId: string,
    type: 'FINGERPRINT' | 'FACE_ID',
    credential: any
  ): Promise<BiometricCredential> {
    const biometricCredential: BiometricCredential = {
      id: `biometric_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      type,
      credentialId: credential.id,
      publicKey: credential.publicKey,
      counter: 0,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        deviceName: this.getDeviceName()
      },
      isActive: true,
      createdAt: new Date()
    };

    const credentials = this.getBiometricCredentials(userId);
    credentials.push(biometricCredential);
    this.saveBiometricCredentials(userId, credentials);

    return biometricCredential;
  }

  async authenticateWithBiometric(
    userId: string,
    credentialId: string,
    signature: any
  ): Promise<boolean> {
    const credentials = this.getBiometricCredentials(userId);
    const credential = credentials.find(c => c.credentialId === credentialId && c.isActive);

    if (!credential) {
      return false;
    }

    // In a real implementation, this would verify the cryptographic signature
    const isValid = this.verifyBiometricSignature(credential, signature);
    
    if (isValid) {
      credential.counter++;
      credential.lastUsed = new Date();
      this.saveBiometricCredentials(userId, credentials);
      
      await this.logAuthenticationAttempt(userId, 'BIOMETRIC', true);
    } else {
      await this.logAuthenticationAttempt(userId, 'BIOMETRIC', false, 'Invalid biometric signature');
    }

    return isValid;
  }

  async removeBiometric(userId: string, credentialId: string): Promise<void> {
    const credentials = this.getBiometricCredentials(userId);
    const updatedCredentials = credentials.filter(c => c.credentialId !== credentialId);
    this.saveBiometricCredentials(userId, updatedCredentials);
  }

  // Risk Assessment
  async assessLoginRisk(userId: string, loginData: {
    ipAddress: string;
    userAgent: string;
    location?: any;
  }): Promise<{
    riskScore: number;
    factors: string[];
    requireAdditionalAuth: boolean;
  }> {
    const factors: string[] = [];
    let riskScore = 0;

    // Check for new device
    const knownDevices = await this.getKnownDevices(userId);
    const deviceFingerprint = this.generateDeviceFingerprint(loginData.userAgent);
    
    if (!knownDevices.includes(deviceFingerprint)) {
      riskScore += 30;
      factors.push('New device');
    }

    // Check for unusual location
    const recentLocations = await this.getRecentLocations(userId);
    if (loginData.location && !this.isKnownLocation(loginData.location, recentLocations)) {
      riskScore += 25;
      factors.push('Unusual location');
    }

    // Check for suspicious IP
    if (await this.isSuspiciousIP(loginData.ipAddress)) {
      riskScore += 40;
      factors.push('Suspicious IP address');
    }

    // Check recent failed attempts
    const recentFailures = await this.getRecentFailedAttempts(userId);
    if (recentFailures > 3) {
      riskScore += 20;
      factors.push('Recent failed login attempts');
    }

    // Check time-based patterns
    const isUnusualTime = await this.isUnusualLoginTime(userId);
    if (isUnusualTime) {
      riskScore += 15;
      factors.push('Unusual login time');
    }

    return {
      riskScore: Math.min(100, riskScore),
      factors,
      requireAdditionalAuth: riskScore >= 50
    };
  }

  // Security Settings Management
  async getSecuritySettings(userId: string): Promise<SecuritySettings> {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_security_${userId}`);
    
    if (stored) {
      return JSON.parse(stored);
    }

    // Default security settings
    const defaultSettings: SecuritySettings = {
      userId,
      twoFactorRequired: false,
      biometricEnabled: false,
      sessionTimeout: 60, // 1 hour
      allowedDevices: [],
      trustedIPs: [],
      securityNotifications: true,
      loginAlerts: true,
      suspiciousActivityAlerts: true,
      updatedAt: new Date()
    };

    this.saveSecuritySettings(defaultSettings);
    return defaultSettings;
  }

  async updateSecuritySettings(
    userId: string,
    updates: Partial<SecuritySettings>
  ): Promise<SecuritySettings> {
    const current = await this.getSecuritySettings(userId);
    const updated = {
      ...current,
      ...updates,
      updatedAt: new Date()
    };

    this.saveSecuritySettings(updated);
    return updated;
  }

  // Authentication Logging
  async logAuthenticationAttempt(
    userId: string,
    method: 'PASSWORD' | 'TWO_FACTOR' | 'BIOMETRIC' | 'SOCIAL',
    success: boolean,
    failureReason?: string
  ): Promise<void> {
    const attempt: AuthenticationAttempt = {
      id: `auth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      method,
      success,
      ipAddress: await this.getCurrentIP(),
      userAgent: navigator.userAgent,
      location: await this.getCurrentLocation(),
      riskScore: 0, // Would be calculated based on various factors
      timestamp: new Date(),
      failureReason
    };

    const attempts = this.getAuthenticationAttempts(userId);
    attempts.push(attempt);
    
    // Keep only last 100 attempts
    if (attempts.length > 100) {
      attempts.splice(0, attempts.length - 100);
    }
    
    this.saveAuthenticationAttempts(userId, attempts);

    // Send security notifications if enabled
    if (!success) {
      await this.sendSecurityAlert(userId, 'failed_login', attempt);
    }
  }

  getAuthenticationHistory(userId: string, limit: number = 20): AuthenticationAttempt[] {
    const attempts = this.getAuthenticationAttempts(userId);
    return attempts
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  // Helper Methods
  private generateTOTPSecret(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  }

  private generateQRCode(userId: string, secret: string): string {
    // In a real app, this would generate an actual QR code
    const issuer = 'TaskBridge';
    const label = `${issuer}:${userId}`;
    const otpauth = `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}`;
    return `data:image/svg+xml;base64,${btoa(`<svg>QR Code for: ${otpauth}</svg>`)}`;
  }

  private generateBackupCodes(): string[] {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      codes.push(Math.random().toString(36).substr(2, 8).toUpperCase());
    }
    return codes;
  }

  private verifyTOTP(secret: string, code: string): boolean {
    // Mock TOTP verification - in real app, use proper TOTP library
    const timeStep = Math.floor(Date.now() / 30000);
    const expectedCode = this.generateTOTP(secret, timeStep);
    return code === expectedCode;
  }

  private generateTOTP(secret: string, timeStep: number): string {
    // Mock TOTP generation - in real app, use proper TOTP algorithm
    const hash = this.simpleHash(secret + timeStep.toString());
    return (parseInt(hash.substr(-6), 16) % 1000000).toString().padStart(6, '0');
  }

  private verifyOTPCode(userId: string, code: string): boolean {
    // Mock OTP verification - in real app, verify against sent code
    const storedCode = localStorage.getItem(`otp_${userId}`);
    return storedCode === code;
  }

  private verifyBackupCode(userId: string, code: string): boolean {
    const setup = this.getTwoFactorSetup(userId);
    if (!setup || !setup.backupCodes) return false;

    const index = setup.backupCodes.indexOf(code);
    if (index !== -1) {
      // Remove used backup code
      setup.backupCodes.splice(index, 1);
      this.saveTwoFactorSetup(userId, setup);
      return true;
    }
    return false;
  }

  private isBiometricSupported(type: string): boolean {
    // Check if WebAuthn is supported
    return !!(navigator.credentials && navigator.credentials.create);
  }

  private generateChallenge(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private createCredentialOptions(userId: string, challenge: string, type: string): any {
    return {
      challenge: new TextEncoder().encode(challenge),
      rp: {
        name: 'TaskBridge',
        id: window.location.hostname
      },
      user: {
        id: new TextEncoder().encode(userId),
        name: userId,
        displayName: userId
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },   // ES256
        { alg: -257, type: 'public-key' }  // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: type === 'FINGERPRINT' ? 'platform' : 'cross-platform',
        userVerification: 'required'
      },
      timeout: 60000,
      attestation: 'direct'
    };
  }

  private getDeviceName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('iPhone')) return 'iPhone';
    if (ua.includes('iPad')) return 'iPad';
    if (ua.includes('Android')) return 'Android Device';
    if (ua.includes('Mac')) return 'Mac';
    if (ua.includes('Windows')) return 'Windows PC';
    return 'Unknown Device';
  }

  private verifyBiometricSignature(credential: BiometricCredential, signature: any): boolean {
    // Mock signature verification - in real app, use proper cryptographic verification
    return Math.random() > 0.1; // 90% success rate for demo
  }

  private generateDeviceFingerprint(userAgent: string): string {
    return this.simpleHash(userAgent + navigator.platform + screen.width + screen.height);
  }

  private isKnownLocation(location: any, recentLocations: any[]): boolean {
    // Mock location checking - in real app, use proper geolocation comparison
    return recentLocations.some(loc => 
      Math.abs(loc.lat - location.lat) < 0.1 && 
      Math.abs(loc.lng - location.lng) < 0.1
    );
  }

  private async isSuspiciousIP(ipAddress: string): Promise<boolean> {
    // Mock suspicious IP checking - in real app, use IP reputation services
    const suspiciousIPs = ['192.168.1.100', '10.0.0.1']; // Mock list
    return suspiciousIPs.includes(ipAddress);
  }

  private async isUnusualLoginTime(userId: string): Promise<boolean> {
    // Mock unusual time checking - in real app, analyze user's login patterns
    const hour = new Date().getHours();
    return hour < 6 || hour > 23; // Outside normal hours
  }

  private simpleHash(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }

  private async getCurrentIP(): Promise<string> {
    // Mock IP detection - in real app, use proper IP detection service
    return '192.168.1.1';
  }

  private async getCurrentLocation(): Promise<any> {
    // Mock location detection - in real app, use geolocation API
    return { country: 'Ireland', city: 'Dublin', lat: 53.3498, lng: -6.2603 };
  }

  private async sendSecurityAlert(userId: string, type: string, data: any): Promise<void> {
    // Mock security alert - in real app, send actual notifications
    console.log(`Security alert for user ${userId}: ${type}`, data);
  }

  // Storage Methods
  private getTwoFactorSetup(userId: string): TwoFactorSetup | null {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_2fa_${userId}`);
    return stored ? JSON.parse(stored) : null;
  }

  private saveTwoFactorSetup(userId: string, setup: TwoFactorSetup): void {
    localStorage.setItem(`${this.STORAGE_KEY}_2fa_${userId}`, JSON.stringify(setup));
  }

  private getBiometricCredentials(userId: string): BiometricCredential[] {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_biometric_${userId}`);
    return stored ? JSON.parse(stored) : [];
  }

  private saveBiometricCredentials(userId: string, credentials: BiometricCredential[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_biometric_${userId}`, JSON.stringify(credentials));
  }

  private saveSecuritySettings(settings: SecuritySettings): void {
    localStorage.setItem(`${this.STORAGE_KEY}_security_${settings.userId}`, JSON.stringify(settings));
  }

  private getAuthenticationAttempts(userId: string): AuthenticationAttempt[] {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_attempts_${userId}`);
    return stored ? JSON.parse(stored) : [];
  }

  private saveAuthenticationAttempts(userId: string, attempts: AuthenticationAttempt[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_attempts_${userId}`, JSON.stringify(attempts));
  }

  // Mock helper methods
  private async verifyPassword(userId: string, password: string): Promise<boolean> {
    // Mock password verification
    return password.length >= 8;
  }

  private async getUserPhoneNumber(userId: string): Promise<string | null> {
    // Mock phone number retrieval
    return '+353 1 234 5678';
  }

  private async getUserEmail(userId: string): Promise<string> {
    // Mock email retrieval
    return 'user@example.com';
  }

  private async getKnownDevices(userId: string): Promise<string[]> {
    // Mock known devices
    return [];
  }

  private async getRecentLocations(userId: string): Promise<any[]> {
    // Mock recent locations
    return [];
  }

  private async getRecentFailedAttempts(userId: string): Promise<number> {
    const attempts = this.getAuthenticationAttempts(userId);
    const recent = attempts.filter(a => 
      !a.success && 
      a.timestamp.getTime() > Date.now() - 24 * 60 * 60 * 1000
    );
    return recent.length;
  }
}

export const authenticationService = new AuthenticationService();
export type { TwoFactorSetup, BiometricCredential, AuthenticationAttempt, SecuritySettings };